import i18n from 'i18next';
import moment from 'moment';

import {
  EXTERNAL_REFERENCE_MAX_LENGTH,
  isExternalReferenceTooLong,
} from 'horizon/components/Damages/ExternalReference/utils';
import {
  type AtlasGqlDamageSchema,
  type AtlasGqlDamageSchemaAttributeDefinition,
  type AtlasGqlDamageSchemaAttributeDefinitionDate,
  type AtlasGqlDamageSchemaAttributeDefinitionNumber,
  type AtlasGqlDamageSchemaAttributeDefinitionString,
  AtlasGqlDamageSchemaAttributeState,
  AtlasGqlDamageSchemaAttributeType,
} from 'types/atlas-graphql';
import { uuidRegex } from 'utils/constants';

import type {
  CsvDamageRowValidation,
  DataField,
  EditableCsvDamageRow,
  HeaderMappingEntry,
  PreviewDamage,
} from '../types';

import { FIELD_IDS } from './common';
import { isValidCoordinates, isValidDateFormat } from './Confirmation/utils';

type DamageSchemaDefinition =
  | AtlasGqlDamageSchemaAttributeDefinition
  | AtlasGqlDamageSchemaAttributeDefinitionDate
  | AtlasGqlDamageSchemaAttributeDefinitionNumber
  | AtlasGqlDamageSchemaAttributeDefinitionString;

export type DamageSchemaForValidation = Pick<AtlasGqlDamageSchema, 'definitions'>;

const ISO_DATE_FORMAT = 'YYYY-MM-DD';
const INSPECTION_ID_PATTERN = new RegExp(`^${uuidRegex}$`, 'i');
const EXTERNAL_REFERENCE_FIELD_IDS = new Set([FIELD_IDS.externalId, FIELD_IDS.externalIdSource]);
const NON_DAMAGE_ATTRIBUTE_FIELD_IDS = new Set([
  FIELD_IDS.imageName,
  FIELD_IDS.coordinates,
  FIELD_IDS.inspectionDate,
  FIELD_IDS.siteName,
  FIELD_IDS.turbineName,
  FIELD_IDS.componentName,
]);
const DAMAGE_INGESTION_V2_FIELD_IDS = new Set([
  ...EXTERNAL_REFERENCE_FIELD_IDS,
  FIELD_IDS.inspectionId,
]);
const INSPECTION_LOOKUP_FIELD_IDS = new Set([
  FIELD_IDS.inspectionDate,
  FIELD_IDS.siteName,
  FIELD_IDS.turbineName,
]);
const NON_DAMAGE_ATTRIBUTE_FIELD_NAMES = new Set([
  'photo file name',
  'coordinates',
  'inspection date',
  'site',
  'turbine',
  'component name',
]);
const LOOKUP_FIELD_ID_PREFIX = 'LOOKUP_';
const NON_VALIDATED_SCHEMA_ATTRIBUTE_NAMES = new Set(['zeitview id']);

const addCellError = (
  cellErrors: Record<string, string[]>,
  schemaFieldId: string,
  message: string
) => {
  cellErrors[schemaFieldId] = [...(cellErrors[schemaFieldId] ?? []), message];
};

const isEmpty = (value: string | undefined) => !value || value.trim() === '';

const definitionAllowsCsvUpload = (definition: DamageSchemaDefinition) =>
  !definition.disabled && definition.state !== AtlasGqlDamageSchemaAttributeState.Disabled;

const shouldSkipSchemaValidation = (
  definition: DamageSchemaDefinition,
  isOnDamageIngestionV2: boolean
) =>
  !isOnDamageIngestionV2 &&
  NON_VALIDATED_SCHEMA_ATTRIBUTE_NAMES.has(definition.name.trim().toLowerCase());

export const isFieldRequired = (
  { key, required }: Pick<DataField, 'key' | 'required'>,
  hasInspectionId: boolean
) => !!required && !(hasInspectionId && INSPECTION_LOOKUP_FIELD_IDS.has(key));

export const isDamageLookupSchemaDefinition = (
  { id, name }: Pick<DamageSchemaDefinition, 'id' | 'name'>,
  isOnDamageIngestionV2 = false
) =>
  id.startsWith(LOOKUP_FIELD_ID_PREFIX) ||
  NON_DAMAGE_ATTRIBUTE_FIELD_IDS.has(id) ||
  (isOnDamageIngestionV2 && DAMAGE_INGESTION_V2_FIELD_IDS.has(id)) ||
  NON_DAMAGE_ATTRIBUTE_FIELD_NAMES.has(name.trim().toLowerCase());

const getDamageSchemaDefinition = (
  schema: DamageSchemaForValidation | undefined,
  schemaFieldId: string,
  isOnDamageIngestionV2: boolean
): DamageSchemaDefinition | undefined => {
  const definition = schema?.definitions?.find(({ id }) => id === schemaFieldId) as
    DamageSchemaDefinition | undefined;

  return definition && !isDamageLookupSchemaDefinition(definition, isOnDamageIngestionV2)
    ? definition
    : undefined;
};

export const getDamageSchemaStringOptions = (
  schema: DamageSchemaForValidation | undefined,
  schemaFieldId: string,
  isOnDamageIngestionV2 = false
): string[] | undefined => {
  const definition = getDamageSchemaDefinition(schema, schemaFieldId, isOnDamageIngestionV2);

  if (
    !definition ||
    shouldSkipSchemaValidation(definition, isOnDamageIngestionV2) ||
    !definitionAllowsCsvUpload(definition) ||
    definition.type !== AtlasGqlDamageSchemaAttributeType.String
  ) {
    return undefined;
  }

  return (definition as AtlasGqlDamageSchemaAttributeDefinitionString).options;
};

const validateDateRange = (
  value: string,
  definition: AtlasGqlDamageSchemaAttributeDefinitionDate
) => {
  const date = moment(value, ISO_DATE_FORMAT, true);
  const min = definition.dateMin ? moment(definition.dateMin) : undefined;
  const max = definition.dateMax ? moment(definition.dateMax) : undefined;

  if (!date.isValid()) {
    return i18n.t('inspection.dateMustUseFormat', 'Date must use {{format}}.', {
      format: ISO_DATE_FORMAT,
    });
  }

  if (min?.isValid() && date.isBefore(min, 'day')) {
    return i18n.t('inspection.dateMustBeOnOrAfter', 'Date must be on or after {{date}}.', {
      date: min.format(ISO_DATE_FORMAT),
    });
  }

  if (max?.isValid() && date.isAfter(max, 'day')) {
    return i18n.t('inspection.dateMustBeOnOrBefore', 'Date must be on or before {{date}}.', {
      date: max.format(ISO_DATE_FORMAT),
    });
  }

  return undefined;
};

const validateSchemaDefinitionValue = (
  value: string,
  definition: DamageSchemaDefinition
): string | undefined => {
  if (!definitionAllowsCsvUpload(definition)) {
    return i18n.t(
      'inspection.attributeNotActive',
      '{{name}} is not an active Damage Schema attribute.',
      { name: definition.name }
    );
  }

  if (definition.type === AtlasGqlDamageSchemaAttributeType.String) {
    const stringDefinition = definition as AtlasGqlDamageSchemaAttributeDefinitionString;
    return stringDefinition.options.includes(value)
      ? undefined
      : i18n.t('inspection.attributeMustBeOneOf', '{{name}} must be one of: {{options}}.', {
          name: definition.name,
          options: stringDefinition.options.join(', '),
        });
  }

  if (definition.type === AtlasGqlDamageSchemaAttributeType.Number) {
    const numberValue = Number(value);
    const numberDefinition = definition as AtlasGqlDamageSchemaAttributeDefinitionNumber;

    if (!Number.isFinite(numberValue)) {
      return i18n.t('inspection.attributeMustBeNumber', '{{name}} must be a number.', {
        name: definition.name,
      });
    }

    if (numberValue < numberDefinition.numMin || numberValue > numberDefinition.numMax) {
      return i18n.t(
        'inspection.attributeMustBeBetween',
        '{{name}} must be between {{min}} and {{max}}.',
        { name: definition.name, min: numberDefinition.numMin, max: numberDefinition.numMax }
      );
    }

    return undefined;
  }

  if (definition.type === AtlasGqlDamageSchemaAttributeType.Date) {
    return validateDateRange(value, definition as AtlasGqlDamageSchemaAttributeDefinitionDate);
  }

  return undefined;
};

const normalizeCsvCellValue = (value: unknown) =>
  String(value ?? '')
    .replace(/\uFEFF/g, '')
    .replace(/\u00A0/g, ' ')
    .trim();

export const isBlankCsvDamageRow = (row: PreviewDamage) =>
  Object.values(row).every((value) => normalizeCsvCellValue(value) === '');

export const filterBlankCsvDamageRows = (rows: PreviewDamage[]) =>
  rows.filter((row) => !isBlankCsvDamageRow(row));

export const createEditableCsvRows = (rows: PreviewDamage[]): EditableCsvDamageRow[] =>
  rows.map((row, index) => ({
    id: `${index}`,
    originalIndex: index,
    values: Object.fromEntries(
      Object.entries(row).map(([key, value]) => [key, value === undefined ? '' : String(value)])
    ),
    validation: {
      isValid: true,
      cellErrors: {},
    },
  }));

export const validateCsvDamageRow = ({
  row,
  dataFields,
  headerMapping,
  schema,
  isOnDamageIngestionV2 = false,
}: {
  row: EditableCsvDamageRow;
  dataFields: DataField[];
  headerMapping: HeaderMappingEntry[];
  schema?: DamageSchemaForValidation;
  isOnDamageIngestionV2?: boolean;
}): CsvDamageRowValidation => {
  const cellErrors: Record<string, string[]> = {};
  const inspectionIdHeader = isOnDamageIngestionV2
    ? headerMapping.find(({ schemaFieldId }) => schemaFieldId === FIELD_IDS.inspectionId)
        ?.fileHeader
    : undefined;
  const hasInspectionId = !!inspectionIdHeader && !isEmpty(row.values[inspectionIdHeader]);

  for (const { fileHeader, schemaFieldId } of headerMapping) {
    const field = dataFields.find(({ key }) => key === schemaFieldId);
    const value = row.values[fileHeader];
    const label = field?.label ?? fileHeader;

    if (field && isFieldRequired(field, hasInspectionId) && isEmpty(value)) {
      const message = i18n.t('inspection.fieldLabelIsRequired', '{{label}} is required.', {
        label,
      });
      addCellError(cellErrors, schemaFieldId, message);
      continue;
    }

    if (isEmpty(value)) {
      continue;
    }

    if (schemaFieldId === FIELD_IDS.coordinates) {
      if (!isValidCoordinates(value)) {
        const message = i18n.t(
          'inspection.fieldLabelMustBeCoordinatePairs',
          '{{label}} must be absolute coordinate pairs.',
          { label }
        );
        addCellError(cellErrors, schemaFieldId, message);
      }
      continue;
    }

    if (isOnDamageIngestionV2 && EXTERNAL_REFERENCE_FIELD_IDS.has(schemaFieldId)) {
      if (isExternalReferenceTooLong(value)) {
        const message = i18n.t(
          'inspection.fieldLabelMustBeCharactersOrFewer',
          '{{label}} must be {{max}} characters or fewer.',
          { label, max: EXTERNAL_REFERENCE_MAX_LENGTH }
        );
        addCellError(cellErrors, schemaFieldId, message);
      }
      continue;
    }

    if (isOnDamageIngestionV2 && schemaFieldId === FIELD_IDS.inspectionId) {
      if (!INSPECTION_ID_PATTERN.test(value.trim())) {
        const message = i18n.t(
          'inspection.fieldLabelMustBeValidId',
          '{{label}} must be a valid Id.',
          {
            label,
          }
        );
        addCellError(cellErrors, schemaFieldId, message);
      }
      continue;
    }

    if (schemaFieldId === FIELD_IDS.inspectionDate) {
      if (!hasInspectionId && !isValidDateFormat(value)) {
        const message = i18n.t(
          'inspection.fieldLabelMustUseFormat',
          '{{label}} must use {{format}}.',
          {
            label,
            format: ISO_DATE_FORMAT,
          }
        );
        addCellError(cellErrors, schemaFieldId, message);
      }
      continue;
    }

    const definition = getDamageSchemaDefinition(schema, schemaFieldId, isOnDamageIngestionV2);

    if (!definition || shouldSkipSchemaValidation(definition, isOnDamageIngestionV2)) {
      continue;
    }

    const error = validateSchemaDefinitionValue(value, definition);

    if (error) {
      addCellError(cellErrors, schemaFieldId, error);
    }
  }

  const hasUnmappedRequiredField = dataFields.some(
    ({ key, required }) =>
      required && !headerMapping.some(({ schemaFieldId }) => schemaFieldId === key)
  );

  if (inspectionIdHeader && !hasInspectionId && hasUnmappedRequiredField) {
    const label =
      dataFields.find(({ key }) => key === FIELD_IDS.inspectionId)?.label ?? inspectionIdHeader;
    const message = i18n.t('inspection.fieldLabelIsRequired', '{{label}} is required.', {
      label,
    });
    addCellError(cellErrors, FIELD_IDS.inspectionId, message);
  }

  return {
    isValid: Object.keys(cellErrors).length === 0,
    cellErrors,
  };
};

export const validateCsvDamageRows = ({
  rows,
  dataFields,
  headerMapping,
  schema,
  isOnDamageIngestionV2 = false,
}: {
  rows: EditableCsvDamageRow[];
  dataFields: DataField[];
  headerMapping: HeaderMappingEntry[];
  schema?: DamageSchemaForValidation;
  isOnDamageIngestionV2?: boolean;
}): EditableCsvDamageRow[] =>
  rows.map((row) => ({
    ...row,
    validation: validateCsvDamageRow({
      row,
      dataFields,
      headerMapping,
      schema,
      isOnDamageIngestionV2,
    }),
  }));

export const trimDamageIngestionV2Values = (
  rows: EditableCsvDamageRow[],
  headerMapping: HeaderMappingEntry[]
): Record<string, string>[] => {
  const fileHeaders = headerMapping
    .filter(({ schemaFieldId }) => DAMAGE_INGESTION_V2_FIELD_IDS.has(schemaFieldId))
    .map(({ fileHeader }) => fileHeader);

  return rows.map(({ values }) => ({
    ...values,
    ...Object.fromEntries(
      fileHeaders.map((fileHeader) => [fileHeader, values[fileHeader]?.trim()])
    ),
  }));
};
